import './agencyOperatorAccess.scss';

import React, { useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { Link, useLocation } from 'react-router-dom';

import { useDialogBehavior } from '../../components/hooks';
import { Button } from '../../components/input/button';
import { useStore } from '../../store/storeHooks';
import { Routes } from '../../routes';
import { createResizeObserver } from '../../core/globals';

const expiryDescription = (daysRemaining: number) => daysRemaining === 0
  ? 'expires today'
  : `expires in ${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'}`;

const tierLabels = { 1: 'Notice', 2: 'Warning', 3: 'Action required' } as const;

export const AgencyOperatorAccessChunk = observer(() => {
  const store = useStore();
  const state = store.agencyOperatorAccess;
  const userId = store.user?.id;
  const location = useLocation();
  const isAuthFlowInProgress = store.authService.shouldWaitForNextContext;

  useEffect(() => {
    state.syncIdentity(userId ?? null);
    if (!userId || isAuthFlowInProgress || state.activePreviewAgencyId) return;
    void state.load(userId).then(() => state.routeChanged());
  }, [isAuthFlowInProgress, location.key, state, state.activePreviewAgencyId, userId]);

  const warning = userId && !isAuthFlowInProgress
    && !state.activePreviewAgencyId ? state.visibleWarning : undefined;
  const previewRef = useRef<HTMLDivElement>(null);
  const hasPreview = Boolean(state.activePreviewAgencyId);
  useEffect(() => {
    const banner = previewRef.current;
    if (!banner) return;
    const style = document.documentElement.style;
    const updateHeight = () => style.setProperty('--agency-preview-banner-height', `${banner.getBoundingClientRect().height}px`);
    const resizeObserver = createResizeObserver(updateHeight);
    updateHeight();
    resizeObserver?.observe(banner);
    return () => {
      resizeObserver?.disconnect();
      style.removeProperty('--agency-preview-banner-height');
    };
  }, [hasPreview]);
  const warningRef = useRef<HTMLDivElement>(null);
  const hasWarning = Boolean(warning);
  useEffect(() => {
    const banner = warningRef.current;
    if (!banner) return;
    const style = document.documentElement.style;
    const updateHeight = () => style.setProperty('--agency-operator-warning-height', `${banner.getBoundingClientRect().height}px`);
    const resizeObserver = createResizeObserver(updateHeight);
    updateHeight();
    resizeObserver?.observe(banner);
    return () => {
      resizeObserver?.disconnect();
      style.removeProperty('--agency-operator-warning-height');
    };
  }, [hasWarning]);
  return (
    <>
      {state.activePreviewAgencyId && (
        <div ref={previewRef} className="agency-preview-banner" role="status" aria-label="Agency preview">
          <strong>Previewing {state.previewAgencyName ?? 'agency'} in read-only mode</strong>
          <span>Changes are disabled while preview is active.</span>
          <button type="button" onClick={() => void state.endPreview()} disabled={state.isPreviewChanging}>
            {state.isPreviewChanging ? 'Ending preview…' : 'End preview'}
          </button>
        </div>
      )}

      {warning && (
        <div ref={warningRef} className={`agency-operator-warning tier-${warning.tier}`} role="status" aria-live="polite">
          <strong className="agency-operator-warning-label">{tierLabels[warning.tier]}</strong>
          <span>
            Operator access to <strong>{warning.agencyName}</strong> {expiryDescription(warning.daysRemaining)}.
            {warning.affectedCount > 1 && ` ${warning.affectedCount} agency grants expire soon.`}
          </span>
          <Link to={`${Routes.account}#agency-operator-access`}>View access details</Link>
          <button
            type="button"
            onClick={() => void state.requestRenewal(warning.agencyId)}
            disabled={state.isRenewalPending(warning.agencyId)}>
            {state.isRenewalPending(warning.agencyId) ? 'Renewal requested' : 'Request renewal'}
          </button>
          {warning.tier === 1 && (
            <button type="button" aria-label="Dismiss warning" onClick={() => state.dismissWarning()}>×</button>
          )}
        </div>
      )}

      {state.isDailyInterstitialOpen && warning?.tier === 3 && (
        <ExpiryInterstitial state={state} />
      )}
      {state.error && <div className="agency-operator-error" role="alert">{state.error}</div>}
    </>
  );
});

const ExpiryInterstitial = observer(({ state }: { state: ReturnType<typeof useStore>['agencyOperatorAccess'] }) => {
  const warning = state.warning!;
  const dialogRef = useDialogBehavior(() => state.closeDailyInterstitial());
  return (
    <div className="agency-operator-interstitial-backdrop">
      <div
        ref={dialogRef}
        className="agency-operator-interstitial"
        role="dialog"
        aria-modal="true"
        aria-labelledby="agency-operator-expiry-title">
        <h2 id="agency-operator-expiry-title">Agency access expires soon</h2>
        <p>
          Your operator access to <strong>{warning.agencyName}</strong> {expiryDescription(warning.daysRemaining)}.
          {warning.affectedCount > 1 && ` ${warning.affectedCount} agency grants expire soon.`}
        </p>
        <div>
          <Button
            type="button"
            className="primary medium"
            onClick={() => void state.requestRenewal(warning.agencyId)}
            disabled={state.isRenewalPending(warning.agencyId)}
            label={state.isRenewalPending(warning.agencyId) ? 'Renewal requested' : 'Request renewal'} />
          <Button className="nav-link medium" onClick={() => state.closeDailyInterstitial()} label="Continue" />
          <Link to={`${Routes.account}#agency-operator-access`} onClick={() => state.closeDailyInterstitial()}>
            View access details
          </Link>
        </div>
      </div>
    </div>
  );
});
